/**
 * HotspotLayer Component
 *
 * Displays DEA (Digital Earth Australia) satellite hotspots on the map.
 * Hotspots are fetched from a static GeoJSON file served via CloudFront,
 * separate from the main events API.
 *
 * IMPORTANT: Hotspots are thermal anomalies detected by satellite.
 * They are NOT confirmed fires - they may be industrial activity,
 * controlled burns, or other heat sources.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { CircleMarker, Popup, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import { UnifiedEvent } from '../types/events';

// Hotspots GeoJSON URL - served from CloudFront
// This URL will be configured via environment variable once CloudFront is deployed
const HOTSPOTS_URL = import.meta.env.VITE_HOTSPOTS_URL || '';

// Refresh interval - matches ingestion (5 minutes)
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface HotspotFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lon, lat]
  };
  properties: {
    id: string;
    title: string;
    description: string;
    region: string;
    observedAt: string;
    confidence: number;
    satellite: string;
    sensor: string;
    tempCelsius: number | null;
    power: number | null;
    hoursSinceDetection: number;
    confidencePercent: number;
  };
}

interface HotspotGeoJSON {
  type: 'FeatureCollection';
  features: HotspotFeature[];
  metadata: {
    source: string;
    generatedAt: string;
    count: number;
    disclaimer: string;
  };
}

interface HotspotLayerProps {
  enabled?: boolean;
  onView3D?: (event: UnifiedEvent) => void;
  onRequestAuth?: () => void;
}

/**
 * Get color based on hotspot confidence level.
 * Higher confidence = more likely to be a real fire.
 */
function getHotspotColor(confidence: number): string {
  if (confidence >= 90) return '#dc2626'; // High confidence - red
  if (confidence >= 70) return '#f97316'; // Medium confidence - orange
  return '#fbbf24'; // Lower confidence - yellow
}

/**
 * Format relative time for display.
 */
function formatTimeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * Check if a hotspot was detected within the last 15 minutes — the
 * window in which a pulsing marker is meaningful as an "active right
 * now" cue. Previously 1 hour, which on a busy fire day put 50-200
 * infinite-pulsing rings on screen and ate compositor time. P0-8 in
 * the 2D perf audit.
 */
function isRecentHotspot(observedAt: string): boolean {
  const date = new Date(observedAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const FIFTEEN_MIN_MS = 15 * 60 * 1000;
  return diffMs <= FIFTEEN_MIN_MS;
}

/**
 * Convert a hotspot feature to a UnifiedEvent for 3D view.
 */
function hotspotToUnifiedEvent(feature: HotspotFeature): UnifiedEvent {
  const [lon, lat] = feature.geometry.coordinates;
  const props = feature.properties;

  return {
    id: `hotspot-${props.id}`,
    type: 'HOTSPOT',
    title: `${props.satellite} Hotspot - ${props.region}`,
    location: { lat, lon },
    region: props.region,
    observedAt: props.observedAt,
    confidence: props.confidence,
    data: {
      satellite: props.satellite,
      sensor: props.sensor,
      confidence: props.confidence,
      confidencePercent: props.confidencePercent,
      tempCelsius: props.tempCelsius,
      power: props.power,
      region: props.region,
    },
  };
}

/**
 * HotspotLayer - displays satellite thermal anomaly detections on the map.
 * No clustering - renders individual CircleMarkers for each hotspot.
 */
export default function HotspotLayer({ enabled = true, onView3D, onRequestAuth }: HotspotLayerProps) {
  const map = useMap();
  const { isAuthenticated } = useAuth();
  const [hotspots, setHotspots] = useState<HotspotGeoJSON | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch hotspots GeoJSON
  useEffect(() => {
    if (!enabled || !HOTSPOTS_URL) {
      setHotspots(null);
      return;
    }

    const fetchHotspots = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(HOTSPOTS_URL, {
          headers: {
            'Accept': 'application/geo+json, application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch hotspots: ${response.status}`);
        }

        const data: HotspotGeoJSON = await response.json();
        setHotspots(data);
        console.log(`[HotspotLayer] Loaded ${data.features.length} hotspots`);
      } catch (err) {
        console.error('[HotspotLayer] Error fetching hotspots:', err);
        setError(err instanceof Error ? err.message : 'Failed to load hotspots');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchHotspots();

    // Set up refresh interval
    const interval = setInterval(fetchHotspots, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled]);

  // Get current viewport bounds for filtering (throttled to reduce updates)
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null);

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const updateBounds = () => {
      // Debounce bounds updates to prevent rapid re-renders during panning
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setViewBounds(map.getBounds());
      }, 150);
    };

    // Initial bounds (immediate)
    setViewBounds(map.getBounds());

    // Update on map movement (debounced)
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter hotspots to viewport with buffer
  const visibleHotspots = useMemo(() => {
    if (!hotspots || !viewBounds) return [];

    // Add 20% buffer for smooth panning
    const latSpan = viewBounds.getNorth() - viewBounds.getSouth();
    const lngSpan = viewBounds.getEast() - viewBounds.getWest();
    const latBuffer = latSpan * 0.2;
    const lngBuffer = lngSpan * 0.2;

    const bufferedBounds = L.latLngBounds(
      [viewBounds.getSouth() - latBuffer, viewBounds.getWest() - lngBuffer],
      [viewBounds.getNorth() + latBuffer, viewBounds.getEast() + lngBuffer]
    );

    return hotspots.features.filter(feature => {
      const [lon, lat] = feature.geometry.coordinates;
      return bufferedBounds.contains([lat, lon]);
    });
  }, [hotspots, viewBounds]);

  if (!enabled || !HOTSPOTS_URL) {
    return null;
  }

  if (isLoading && !hotspots) {
    return null; // Don't show loading indicator on map
  }

  if (error) {
    console.warn('[HotspotLayer] Error state:', error);
    return null; // Fail silently on map
  }

  // Create pulsing div icons for recent hotspots. One ring instead of the
  // previous two (delayed) — second ring doubled compositor cost without
  // adding signal. Animation pauses under prefers-reduced-motion and when
  // the tab is hidden (via the visibilitychange listener at the document
  // level, which pauses CSS animations).
  const createPulsingIcon = (color: string) => {
    return L.divIcon({
      className: 'hotspot-pulse-container',
      html: `<div class="hotspot-pulse-ring" style="border-color: ${color};"></div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  };

  // Render individual CircleMarkers without clustering
  return (
    <>
      {/* CSS keyframes for pulsing animation - injected once.
          Belt-and-braces pointer-events:none on the container AND every
          descendant: the pulsing div icon sits in the marker pane above
          the CircleMarker (overlay pane), and although interactive={false}
          on the Marker is supposed to apply `leaflet-non-interactive`,
          we still saw taps being eaten in production. Explicit CSS leaves
          no room for that to fail. */}
      <style>{`
        .hotspot-pulse-container,
        .hotspot-pulse-container * {
          pointer-events: none !important;
        }
        .hotspot-pulse-container {
          background: transparent !important;
          border: none !important;
        }
        @keyframes hotspot-pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0.9;
          }
          100% {
            transform: translate(-50%, -50%) scale(2.5);
            opacity: 0;
          }
        }
        .hotspot-pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid;
          background: transparent;
          animation: hotspot-pulse 2s ease-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .hotspot-pulse-ring {
            animation: none;
            opacity: 0.6;
          }
        }
      `}</style>

      {visibleHotspots.map((feature) => {
        const [lon, lat] = feature.geometry.coordinates;
        const props = feature.properties;
        const color = getHotspotColor(props.confidencePercent);
        const isRecent = isRecentHotspot(props.observedAt);

        return (
          <React.Fragment key={props.id}>
            {/* Pulsing ring for recent hotspots (last 1 hour) */}
            {isRecent && (
              <Marker
                position={[lat, lon]}
                icon={createPulsingIcon(color)}
                interactive={false}
              />
            )}

            {/* Main hotspot marker. Radius bumped 6→8 so the click target
                (radius+half-stroke) is ~18px instead of ~14px; still small
                visually but tappable on touch. The pulse marker above is
                explicitly pointer-events:none so it can't steal clicks. */}
            <CircleMarker
              center={[lat, lon]}
              radius={8}
              pathOptions={{
                color: color,
                fillColor: color,
                fillOpacity: 0.7,
                weight: 2,
                opacity: 0.9,
              }}
            >
            <Popup maxWidth={300} minWidth={240} className="hotspot-popup-wrapper">
              <div className="hotspot-popup">
                {/* Header - colored banner */}
                <div style={{
                  backgroundColor: color,
                  color: props.confidencePercent >= 90 ? 'white' : 'black',
                  padding: '10px 14px',
                  borderRadius: '8px 8px 0 0',
                }}>
                  <div style={{ fontSize: '10px', opacity: 0.9, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Satellite Hotspot
                  </div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    {props.satellite} ({props.sensor})
                  </div>
                </div>

                {/* Body content */}
                <div style={{ padding: '12px 14px' }}>
                  {/* Confidence badge */}
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{
                      backgroundColor: color,
                      color: props.confidencePercent >= 90 ? 'white' : 'black',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      {props.confidencePercent}% confidence
                    </span>
                  </div>

                  {/* Details */}
                  <div style={{ fontSize: '12px', lineHeight: '1.7' }}>
                    {props.tempCelsius && (
                      <div><strong>Temperature:</strong> {props.tempCelsius}°C</div>
                    )}
                    {props.power != null && props.power > 0 && (
                      <div><strong>Fire Power:</strong> {props.power} MW</div>
                    )}
                    <div><strong>Region:</strong> {props.region}</div>
                    <div><strong>Detected:</strong> {formatTimeAgo(props.observedAt)}</div>
                  </div>

                  {/* Disclaimer */}
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 10px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '10px',
                    color: '#92400e',
                    lineHeight: '1.5',
                  }}>
                    <strong>Note:</strong> Satellite hotspots detect thermal anomalies.
                    Not all hotspots are fires - may be industrial or controlled burns.
                  </div>

                  {/* View in 3D Button */}
                  {(onView3D || onRequestAuth) && (
                    <button
                      onClick={() => {
                        if (isAuthenticated && onView3D) {
                          onView3D(hotspotToUnifiedEvent(feature));
                        } else if (onRequestAuth) {
                          onRequestAuth();
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        width: '100%',
                        marginTop: '12px',
                        padding: '10px 14px',
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        textAlign: 'center',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <span>🌐</span>
                      {isAuthenticated ? 'View in 3D Terrain' : 'Sign in for 3D View'}
                    </button>
                  )}
                </div>
              </div>
            </Popup>
          </CircleMarker>
          </React.Fragment>
        );
      })}
    </>
  );
}
